// UI state as signals. The App controller writes it, the Preact screens read
// it; the game loop never touches it per frame.

import { signal } from '@preact/signals';
import type { RunResult } from '../game/game';
import type { NetClient } from '../game/net';
import { loadSettings, saveSettings, type Settings, type SettingsStore } from '../game/settings';
import type { MapInfo, RemotePlayer, RoomInfo, RoomState } from '../shared/net/protocol';
import type { MapRulesInfo } from './maps/mapMeta';

export type Phase = 'boot' | 'menu' | 'loading' | 'playing' | 'paused';
export type Tab = 'play' | 'rooms' | 'maps' | 'settings' | 'admin';

export interface NetState {
  status: 'offline' | 'connecting' | 'online';
  client: NetClient | null;
  serverName: string;
  /** The server's map catalog. */
  maps: MapInfo[];
  /** Lobby: public rooms (and the one the player is in). */
  rooms: RoomInfo[];
  /** The room the player is in. */
  room: RoomState | null;
  /** Round trip in ms, null while unknown. */
  ping: number | null;
  error: string | null;
  /** When the next connection attempt is made (ms, Date.now()), while offline. */
  retryAt: number | null;
}

export interface GameInfo {
  map: string;
  online: boolean;
  serverName: string | null;
  /** Map info from the server catalog (online). */
  info: MapInfo | null;
  runs: string[];
  rules: MapRulesInfo;
  /** Teams with spawns on the map (Axis 0, Allies 1): the ones a player can join. */
  teams: (0 | 1)[];
}

export interface LoadingState {
  map: string;
  /** What is being done ("Building collision"). */
  stage: string;
  /** 0 .. 1 */
  progress: number;
}

export type ToastKind = 'info' | 'good' | 'bad';
export interface Toast {
  id: number;
  text: string;
  kind: ToastKind;
}

export interface ModalState {
  title: string;
  body: string;
  confirm: string;
  cancel: string;
  danger: boolean;
  resolve: (ok: boolean) => void;
}

export const phase = signal<Phase>('boot');
export const tab = signal<Tab>('play');
/** In the pause menu: the menu itself or the settings opened from it. */
export const pauseView = signal<'main' | 'settings'>('main');
export const settings = signal<Settings>(loadSettings());
/** Bumped when pk3s are added or removed (map list, levelshots). */
export const vfsVersion = signal(0);
/** Bumped when map info was read in the background or a thumbnail was captured. */
export const mapIndexVersion = signal(0);
export const net = signal<NetState>({ status: 'offline', client: null, serverName: '', maps: [], rooms: [], room: null, ping: null, error: null, retryAt: null });
export const game = signal<GameInfo | null>(null);
export const loading = signal<LoadingState | null>(null);
export const toasts = signal<Toast[]>([]);
/** Last finished run of the current map. */
export const runResult = signal<RunResult | null>(null);
/** The floating end-of-run panel is shown. */
export const runResultVisible = signal(false);
export const spectating = signal(false);
/** Map whose details drawer is open (Maps / Online). */
export const selectedMap = signal<string | null>(null);
/** The create room dialog (with the map to preselect). */
export const createRoomDialog = signal<{ map: string | null } | null>(null);
/** Scoreboard held open in game. */
export const scoreboardOpen = signal(false);
/** Other players of the room as of the last snapshot (updated a few times a second). */
export const roomPlayers = signal<RemotePlayer[]>([]);
export const modal = signal<ModalState | null>(null);

let nextToast = 1;

export function toast(text: string, kind: ToastKind = 'info', ms = 4500): void {
  const id = nextToast++;
  toasts.value = [...toasts.value.slice(-3), { id, text, kind }];
  setTimeout(() => dismissToast(id), ms);
}

export function dismissToast(id: number): void {
  toasts.value = toasts.value.filter((t) => t.id !== id);
}

/** Confirmation dialog; resolves true when confirmed. */
export function confirmDialog(opts: { title: string; body: string; confirm?: string; cancel?: string; danger?: boolean }): Promise<boolean> {
  modal.value?.resolve(false);
  return new Promise((resolve) => {
    modal.value = {
      title: opts.title,
      body: opts.body,
      confirm: opts.confirm ?? 'Confirm',
      cancel: opts.cancel ?? 'Cancel',
      danger: opts.danger ?? false,
      resolve: (ok) => {
        modal.value = null;
        resolve(ok);
      },
    };
  });
}

/** The live settings: read by the game every frame, written by the menus (saved right away). */
export const settingsStore: SettingsStore = {
  get: () => settings.peek(),
  update(patch) {
    settings.value = { ...settings.peek(), ...patch };
    saveSettings(settings.peek());
  },
};

export function updateSettings(patch: Partial<Settings>): void {
  settingsStore.update(patch);
}

export function updateHud(patch: Partial<Settings['hud']>): void {
  settingsStore.update({ hud: { ...settings.peek().hud, ...patch } });
}
